import type { ReactNode } from 'react';

/** Design: Review Detail / Merge Decision header band — horizontal 28px. */
export function CpmModalHeader({
    children,
    className = '',
}: {
    children: ReactNode;
    className?: string;
}) {
    return (
        <div
            className={[
                'shrink-0 px-7 pt-4 pb-[18px] border-b border-[var(--cpm-border-default)] bg-[rgba(249,249,252,0.92)] backdrop-blur-[14px]',
                className,
            ].join(' ')}
        >
            {children}
        </div>
    );
}

/**
 * Design: body `padding:24px 28px 40px`, gap 22.
 * Page modals (`clampScroll={false}`): body is the only scroller; header/footer stay fixed.
 */
export function CpmModalBody({
    children,
    className = '',
    /** Card modals clamp; page modals scroll only this region. */
    clampScroll = true,
}: {
    children: ReactNode;
    className?: string;
    clampScroll?: boolean;
}) {
    return (
        <div
            className={[
                'px-7 pt-6 pb-10 flex flex-col gap-[22px]',
                clampScroll
                    ? 'max-h-[70vh] overflow-y-auto [overscroll-behavior:contain]'
                    : 'flex-1 min-h-0 overflow-y-auto [overscroll-behavior:contain]',
                className,
            ].join(' ')}
        >
            {children}
        </div>
    );
}

/**
 * Card modals (default `row`): horizontal footer, pad 16×24, soft bg, no upward shadow.
 * Page modals (`stack`): vertical stack, pad 14×28, upward shadow (Review Detail).
 */
export function CpmModalFooter({
    children,
    className = '',
    layout = 'row',
}: {
    children: ReactNode;
    className?: string;
    layout?: 'row' | 'stack';
}) {
    const isRow = layout === 'row';
    return (
        <div
            className={[
                'shrink-0 border-t-[1.5px] border-[var(--cpm-border-strong)] bg-[rgba(249,249,252,0.88)] backdrop-blur-[14px] rounded-b-[var(--cpm-radius-2xl)]',
                isRow
                    ? 'flex flex-row items-center gap-[10px] px-6 py-4'
                    : 'flex flex-col gap-3 px-7 py-[14px] shadow-[0_-8px_24px_-12px_rgba(14,8,88,.25)]',
                className,
            ].join(' ')}
        >
            {children}
        </div>
    );
}
